import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { Cog6ToothIcon } from '@heroicons/react/24/outline';
import { DialogActions, FormError, Modal, ModalBody, ModalFooter, ModalHeader, PinnableOrderList, ToolbarActions } from '../ui';
import type { PinnableOrderListItem } from '../ui';
import {
  updateDashboardLayout,
  type DashboardLayoutResponse,
  type DashboardWidgetKey,
} from '../../lib/api/dashboardLayout.gen';
import { errorMessage } from '../../lib/errorMessage';

// Dashboard personalization's own UI affordance (story 2-143b, DOD §1.10 —
// "the widget catalog needs a real UI affordance ... not just a backend
// contract with no consumer"). A `Modal` over `PinnableOrderList` (story
// 2-143b's own reorder primitive — see its header for why it is a sibling of
// `OrderedListField` rather than a reuse) rather than an always-visible inline
// panel: pin/unpin + reorder is an occasional, deliberate action (the same
// "occasional management" class P10 §2's disclosure rule already assigns to a
// `Modal`), not something that should compete for space with the dashboard's
// own primary content on every load.
//
// `layout` is READ from the caller (`DashboardPage.tsx`'s own `GET
// /v1/dashboard-layout` query) rather than fetched a second time here — one
// query, shared by both "what order do I render the page in" and "what does
// the customize dialog show", exactly the way `ActivationChecklistCard`'s own
// query key is shared with nothing else because it owns its one card; here
// the SAME cache entry legitimately backs two different pieces of UI, so it
// is lifted to the parent instead of duplicated.
export interface DashboardCustomizeControlProps {
  layout: DashboardLayoutResponse | undefined;
  /** The exact react-query key the parent's own `useQuery` reads/writes —
   * passed through so a successful save can `setQueryData` the SAME entry the
   * page itself renders from, without a second round trip. */
  queryKey: QueryKey;
}

const toItem = (widget: DashboardLayoutResponse['widgets'][number]): PinnableOrderListItem => ({
  key: widget.key,
  label: widget.title,
  description: widget.description,
  pinned: widget.pinned,
  // Server-enforced (`dashboardLayout.service.js`'s own `allowed` gate) — the
  // switch disabling itself here is a courtesy reflecting that fact, not the
  // authority. A widget this principal's role may not see also cannot be
  // toggled ON from this dialog, even before the save round-trips.
  disabled: !widget.allowed,
  ...(widget.reason ? { disabledReason: widget.reason } : {}),
});

export function DashboardCustomizeControl({ layout, queryKey }: DashboardCustomizeControlProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PinnableOrderListItem[] | null>(null);

  const mutation = useMutation({
    mutationFn: (widgets: Array<{ key: DashboardWidgetKey; pinned: boolean }>) => updateDashboardLayout({ widgets }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKey, next);
      setOpen(false);
      setDraft(null);
    },
  });

  // Nothing to customize until the catalog itself has loaded once — no
  // second loading/error state invented for this button; it simply doesn't
  // render until there is real data to show, matching `ActivationChecklistCard`'s
  // own `if (!user || isLoading || isError || !data) return null` idiom.
  if (!layout) return null;

  const items = draft ?? layout.widgets.map(toItem);

  const close = () => {
    setOpen(false);
    setDraft(null);
    mutation.reset();
  };

  return (
    <>
      <ToolbarActions
        size="sm"
        actions={[
          {
            id: 'customize',
            label: 'Customize',
            variant: 'secondary',
            icon: <Cog6ToothIcon className="h-4 w-4" />,
            onClick: () => setOpen(true),
            'data-testid': 'dashboard-customize',
          },
        ]}
      />
      {open && (
        <Modal onClose={close} label="Customize dashboard">
          <ModalHeader title="Customize dashboard" onClose={close}>
            <p className="mt-1 text-sm text-muted-foreground">
              Pin the sections you want on your dashboard, and reorder them. A locked section is not part of
              your role — ask an org owner or manager.
            </p>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4" data-testid="dashboard-customize-dialog">
              <PinnableOrderList
                id="dashboard-widgets"
                items={items}
                onChange={setDraft}
                disabled={mutation.isPending}
              />
              <FormError message={mutation.isError ? errorMessage(mutation.error) : null} />
            </div>
          </ModalBody>
          <ModalFooter>
            <DialogActions
              size="sm"
              actions={[
                {
                  id: 'cancel',
                  label: 'Cancel',
                  variant: 'ghost',
                  onClick: close,
                  'data-testid': 'dashboard-customize-cancel',
                },
                {
                  id: 'save',
                  label: 'Save',
                  variant: 'primary',
                  loading: mutation.isPending,
                  onClick: () =>
                    mutation.mutate(items.map((item) => ({ key: item.key as DashboardWidgetKey, pinned: item.pinned }))),
                  'data-testid': 'dashboard-customize-save',
                },
              ]}
            />
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
